import * as React from 'react';
import { styled } from '@glitz/react';
import { bareListDecorator } from 'webapp/decorators/list';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';
import { MenuItem } from 'webapp/shell/main-menu/MenuItem';
import { NextLevel } from 'webapp/shell/main-menu/NextLevel';
import { LevelHeading } from 'webapp/shell/main-menu/LevelHeading';
import { LEVEL_WIDTH } from 'webapp/shell/main-menu/helpers';
import { MenuHighlights } from 'nordicnest/shell/main-menu/MenuHighlights';
import { MenuHighlights as SvenssonsMenuHighlights } from 'svenssons/shell/main-menu/MenuHighlights';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { isSvenssons } from 'webapp/theme';

import type { JSX } from "react";

type PropType = {
  menu: NordicNest.Api.CfMenuApiModel;
  close: () => void;
  selectedCategory: NordicNest.Api.MenuItemApiModel;
  activeLevel: number;
  setActiveLevel: (nbr: number) => void;
};

export const DesktopMenu = React.memo(
  ({ menu, close, selectedCategory, activeLevel, setActiveLevel }: PropType): JSX.Element => {
    const [selectedLevel2, setSelectedLevel2] = React.useState<NordicNest.Api.MenuItemApiModel>(
      {} as NordicNest.Api.MenuItemApiModel,
    );
    const [selectedLevel3, setSelectedLevel3] = React.useState<NordicNest.Api.MenuItemApiModel>(
      {} as NordicNest.Api.MenuItemApiModel,
    );
    return (
      <Base>
        {isSvenssons ? (
          <SvenssonsMenuHighlights menu={menu} selectedCategory={selectedCategory} />
        ) : (
          <MenuHighlights menu={menu} close={close} />
        )}
        <Menu>
          <Level>
            <LevelHeading level={selectedCategory} menu_category="level_1" {...NnTestIdProps.MenuLevelHeading} />
            <List>
              {selectedCategory?.childPages?.map((level2, i) => (
                <MenuItem
                  key={level2.link.to + i}
                  level={level2}
                  selectedLevel={selectedLevel2}
                  setSelectedLevel={setSelectedLevel2}
                  setActiveLevel={setActiveLevel}
                  menu_type="side"
                  menu_category="level_2"
                />
              ))}
            </List>
          </Level>
          <NextLevel
            setActiveLevel={setActiveLevel}
            level={selectedLevel2}
            selectedLevel={selectedLevel3}
            setSelectedLevel={setSelectedLevel3}
            menu_category="level_3"
            openLevel={activeLevel >= 3}
          />
          <NextLevel level={selectedLevel3} menu_category="level_4" openLevel={activeLevel === 4} />
        </Menu>
      </Base>
    );
  },
);

const Base = styled.nav({
  display: 'grid',
  gridTemplateColumns: `${LEVEL_WIDTH}px 1fr`,
  gridTemplateRows: '100%',
  flexGrow: 1,
  overflow: 'hidden',
  backgroundColor: t => t.header?.backgroundColor,
});

const Menu = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
});

const Level = styled.div(
  scrollbarDecorator,
  {
    minWidth: LEVEL_WIDTH,
    margin: { y: t => t.sharedSpacing.xxl },
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  !isSvenssons && {
    border: { left: { width: 1, style: 'solid', color: t => t.header?.menuHightlightColor } },
  },
);

const List = styled.ul(bareListDecorator, {
  display: 'flex',
  flexDirection: 'column',
  breakInside: 'avoid',
});
